import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuditLogHealthResponse } from "./types";
import { formatNum } from "./helpers";

const DISMISS_KEY = "admin-audit-log-over-threshold-dismissed-v1";

export function AuditLogHealthBanner({ onView }: { onView: () => void }) {
  const healthQuery = useQuery<AuditLogHealthResponse>({
    queryKey: ["/api/admin/audit-log-health"],
    queryFn: async () => {
      const res = await fetch("/api/admin/audit-log-health", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load audit-log health");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const overThreshold = healthQuery.data?.overThreshold ?? false;

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  // When the condition clears, reset the dismissal so a future breach surfaces
  // the banner again instead of staying permanently hidden.
  useEffect(() => {
    if (!overThreshold && dismissed) {
      setDismissed(false);
      try {
        localStorage.removeItem(DISMISS_KEY);
      } catch {}
    }
  }, [overThreshold, dismissed]);

  if (!overThreshold || dismissed) return null;

  const data = healthQuery.data!;

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  }

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-3 rounded-md border border-red-300 bg-red-50 px-4 py-3 dark:border-red-900 dark:bg-red-950/40"
      data-testid="banner-audit-log-over-threshold"
      role="alert"
    >
      <AlertTriangle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-red-800 dark:text-red-200">
          Audit log is abnormally large
        </p>
        <p className="text-xs text-red-700 dark:text-red-300">
          {formatNum(data.totalRows)} rows exceed the alert threshold of {formatNum(data.alertRowThreshold)}.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-red-300 text-red-800 hover:bg-red-100 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/40"
        onClick={onView}
        data-testid="button-view-audit-log-health"
      >
        View details
        <ArrowRight className="ml-1.5 h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-900/40"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="button-dismiss-audit-log-banner"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
